<template>
    <Card>
        <CardHeader>
            <div class="flex items-center gap-2">
                <ShieldCheck class="size-4 text-muted-foreground" />
                <CardTitle class="text-sm font-medium text-primary">
                    Two Factor Authentication
                </CardTitle>
            </div>
        </CardHeader>

        <CardContent class="space-y-6">
            <div class="space-y-3">
                <p class="text-lg">
                    Two-Factor Authentication is
                    <span v-if="twoFactorEnabled" class="font-semibold text-new-green-600">enabled</span>
                    <span v-else class="font-semibold text-new-red-600">not enabled</span>
                </p>
                <p class="text-xs leading-4 text-muted-foreground max-w-[411px]">
                    When Two-Factor Authentication (2FA) is enabled, when you sign in you'll be required to use the security code provided by your Authenticator App of choice. You can learn more about 2FA in our documentation.
                </p>
            </div>

            <div class="flex gap-3">
                <template v-if="!twoFactorEnabled">
                    <component :is="confirmComponent" :allow-passkey="hasPasskeys" @confirmed="showTwoFactorAuthentication = true">
                        <Button :disabled="loading">
                            Enable 2FA
                        </Button>
                    </component>
                </template>
                <template v-else>
                    <component :is="confirmComponent" :allow-passkey="hasPasskeys" @confirmed="fetchAndShowRecoveryCodes">
                        <Button variant="outline">
                            Show recovery codes
                        </Button>
                    </component>
                    <component :is="confirmComponent" :allow-passkey="hasPasskeys" @confirmed="showPairingCodes = true">
                        <Button variant="outline">
                            Pairing QR code
                        </Button>
                    </component>
                    <component :is="confirmComponent" :allow-passkey="hasPasskeys" @confirmed="disableTwoFactor">
                        <Button :disabled="loading">
                            Disable 2FA
                        </Button>
                    </component>
                </template>
            </div>
        </CardContent>
    </Card>

    <TwoFactorAuthenticationDialogV2
        v-if="showTwoFactorAuthentication"
        :has-password="hasPassword"
        :has-passkeys="hasPasskeys"
        @close="showTwoFactorAuthentication = false"
        @enabled="enabled = true"
    />
    <TwoFactorRecoveryCodesDialogV2
        v-if="showRecoveryCodes"
        title="Recovery Codes"
        :recovery-codes="fetchedRecoveryCodes"
        :can-regenerate="true"
        @close="showRecoveryCodes = false"
    />
    <TwoFactorPairingCodesDialogV2
        v-if="showPairingCodes"
        @close="showPairingCodes = false"
    />
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import axios from 'axios';
import { toast } from 'vue-sonner';
import { ShieldCheck } from 'lucide-vue-next';
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import ConfirmsPassword from './ConfirmsPasswordV2.vue';
import ConfirmsOtp from './ConfirmsOtpV2.vue';
import TwoFactorAuthenticationDialogV2 from './TwoFactorAuthenticationDialogV2.vue';
import TwoFactorRecoveryCodesDialogV2 from './TwoFactorRecoveryCodesDialogV2.vue';
import TwoFactorPairingCodesDialogV2 from './TwoFactorPairingCodesDialogV2.vue';

const props = withDefaults(defineProps<{
    hasPassword: boolean;
    hasPasskeys?: boolean;
}>(), {
    hasPasskeys: false,
});

const globalProps = computed(() => usePage<ITopolPageProps>().props);

const confirmComponent = computed(() =>
    props.hasPassword ? ConfirmsPassword : ConfirmsOtp
);

const loading = ref(false);
const enabled = ref(false);
const showTwoFactorAuthentication = ref(false);
const showRecoveryCodes = ref(false);
const showPairingCodes = ref(false);
const fetchedRecoveryCodes = ref<string[]>([]);

const twoFactorEnabled = computed(() => {
    return globalProps.value.user.two_factor_enabled || enabled.value;
});

const fetchAndShowRecoveryCodes = async () => {
    try {
        const res = await axios.get('/user/two-factor-recovery-codes');
        fetchedRecoveryCodes.value = res.data;
        showRecoveryCodes.value = true;
    } catch {
        toast.error('Failed to fetch recovery codes');
    }
};

const disableTwoFactor = () => {
    loading.value = true;
    router.delete('/user/two-factor-authentication', {
        preserveScroll: true,
        onSuccess: () => {
            loading.value = false;
            enabled.value = false;
            toast.success('Two-Factor Authentication disabled');
        },
        onError: () => {
            loading.value = false;
            toast.error('Failed to disable 2FA');
        },
    });
};
</script>
